import { useState } from "react";
import { X, UploadCloud } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { QUALIFICATION_LABELS } from "@/pages/Apply";

const QUALIFICATION_OPTIONS = Object.keys(QUALIFICATION_LABELS);

/**
 * Lets any counsellor propose changes to their specialty/bio/qualification.
 * Applied immediately for the lead admin or an admin-generated ("bypass")
 * account; otherwise queued for admin approval (POST /counselor/profile-edit).
 */
export default function EditProfileModal({ user, onClose, onSaved }) {
  const bypass = !!(user?.is_lead || user?.admin_generated);
  const [specialty, setSpecialty] = useState(user?.specialty || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [qualificationPath, setQualificationPath] = useState(user?.qualification_path && QUALIFICATION_OPTIONS.includes(user.qualification_path) ? user.qualification_path : "");
  const [otherDetails, setOtherDetails] = useState("");
  const [certUrls, setCertUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const uploadCert = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCertUrls(prev => [...prev, data.url]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!specialty.trim() || !bio.trim()) { toast.error("Specialty and bio are required."); return; }
    if (qualificationPath === "other" && !otherDetails.trim()) {
      toast.error("Please describe your other qualification details."); return;
    }
    if (qualificationPath && qualificationPath !== "other" && certUrls.length === 0) {
      toast.error("Please attach your qualification certificate."); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/counselor/profile-edit", {
        specialty: specialty.trim(), bio: bio.trim(),
        qualification_path: qualificationPath || null,
        qualification_certificate_urls: certUrls,
        other_qualification_details: qualificationPath === "other" ? otherDetails.trim() : null,
      });
      if (data.applied) toast.success("Profile updated.");
      else toast.success("Submitted — your admin will review this change shortly.");
      onSaved?.(data);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: "rgba(28,33,30,0.45)" }} data-testid="edit-profile-modal">
      <div className="card-soft max-w-lg w-full p-7 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif-display text-2xl">Edit profile</h2>
          <button data-testid="edit-profile-close" onClick={onClose} className="btn-outline p-2"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs mb-5" style={{ color: "var(--text-secondary)" }}>
          {bypass
            ? "Changes here apply immediately."
            : "Changes to your specialty, bio, or qualification are reviewed by admin before they go live."}
        </p>

        <label className="label-eyebrow block mb-2">Specialty</label>
        <input data-testid="edit-profile-specialty" value={specialty} onChange={e => setSpecialty(e.target.value)}
          className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-2.5 mb-4 focus:outline-none focus:border-[color:var(--brand)]" />

        <label className="label-eyebrow block mb-2">Bio</label>
        <textarea data-testid="edit-profile-bio" rows={4} value={bio} onChange={e => setBio(e.target.value)}
          className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-2.5 mb-4 resize-none focus:outline-none focus:border-[color:var(--brand)]" />

        <label className="label-eyebrow block mb-2">Qualification (optional update)</label>
        <select data-testid="edit-profile-qualification" value={qualificationPath} onChange={e => setQualificationPath(e.target.value)}
          className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-2.5 mb-3 bg-white">
          <option value="">Leave unchanged</option>
          {QUALIFICATION_OPTIONS.map(k => <option key={k} value={k}>{QUALIFICATION_LABELS[k]}</option>)}
        </select>

        {qualificationPath === "other" && (
          <textarea data-testid="edit-profile-other-details" rows={2} placeholder="Describe your qualification"
            value={otherDetails} onChange={e => setOtherDetails(e.target.value)}
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-2.5 mb-3 resize-none focus:outline-none focus:border-[color:var(--brand)]" />
        )}

        {qualificationPath && qualificationPath !== "other" && (
          <div className="mb-3">
            <label className="btn-outline text-xs inline-flex items-center gap-1 cursor-pointer" data-testid="edit-profile-cert-upload">
              <UploadCloud className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Attach certificate"}
              <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" disabled={uploading}
                onChange={e => { const f = e.target.files?.[0]; if (f) uploadCert(f); e.target.value = ""; }} />
            </label>
            {certUrls.length > 0 && <span className="text-xs ml-2" style={{ color: "var(--brand)" }}>{certUrls.length} file(s) attached</span>}
          </div>
        )}

        <button data-testid="edit-profile-submit" onClick={submit} disabled={busy}
          className="btn-primary w-full mt-2 disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? "Saving…" : bypass ? "Save changes" : "Submit for approval"}
        </button>
      </div>
    </div>
  );
}
